import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Coins, Zap, ShoppingCart, Check, Info, Trophy, Gift, KeyRound, Wand2, Shield, Star, Lock, Filter, Package
} from "lucide-react";
import { useFuncion } from "../context/FuncionContext";

/* ===== Utils ===== */
const toNumber = (v, d = 0) => {
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : d;
};
const pretty = (n) => new Intl.NumberFormat().format(toNumber(n, 0));

/* ===== Catálogo ===== */
const ICONS = {
  comodin: Wand2,
  boost: Zap,
  cosmético: Star,
  skin: Gift,
  pase: KeyRound,
  proteccion: Shield,
  item: Package
};

// Normaliza items del backend sin tocar tu Context
const normalizeItem = (raw = {}) => ({
  id: raw.id,
  nombre: raw.nombre || raw.name,
  descripcion: raw.descripcion || raw.description || "",
  tipo: String(raw.tipo || raw.categoria || raw.category || "item").toLowerCase(),
  precio_coins: toNumber(raw.precio_coins ?? raw.price_coins ?? raw.price ?? 0),
  active: (raw.activo ?? raw.active ?? true) === true,
  stock: raw.stock_infinito === true ? null : (raw.stock_actual ?? raw.stock ?? null),
  limit_per_user: raw.limit_per_user ?? raw.limit ?? null,
  badge: raw.badge ?? null,
  owned_count: toNumber(raw.owned_count ?? raw.owned ?? 0),
});

function useCatalog() {
  const { fetchStoreItems, getMyStorePurchases } = useFuncion();
  const [state, setState] = useState({ items: [], orders: [], loading: true, err: "" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [itemsRes, ordersRes] = await Promise.all([
          fetchStoreItems?.({}).catch(() => ({})),
          getMyStorePurchases?.({ limit: 10 }).catch(() => ({}))
        ]);

        const rawItems = Array.isArray(itemsRes?.items) ? itemsRes.items : (itemsRes?.products || []);
        const items = rawItems.map(normalizeItem);

        const orders = Array.isArray(ordersRes?.items) ? ordersRes.items : (ordersRes?.purchases || []);

        if (!alive) return;
        setState({ items, orders, loading: false, err: "" });
      } catch (e) {
        if (!alive) return;
        setState((s) => ({ ...s, loading: false, err: e?.message || "No se pudo cargar la tienda." }));
      }
    })();
    return () => { alive = false; };
  }, [fetchStoreItems, getMyStorePurchases]);

  return state;
}

/* ===== Componente principal ===== */
const Store = () => {

  const navigate = useNavigate();
  const location = useLocation();

  const goBack = useCallback(() => {
    const from = location.state?.from;
    if (from) {
      navigate(from);
      return;
    }
    // fallback: si no hay "from", intentamos volver en historial, y si no, vamos a Home
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  }, [location.state, navigate]);

  const {
    utmCoins,
    studentStreak,
    initializeStudentData,
    buyStoreItem,
    refreshUTMCoins,
  } = useFuncion();

  /* == Estado de racha local (como en CursoDetalle/ComponenteTareas) == */


  // === Datos generales ===
  useEffect(() => {
    if (!utmCoins || !studentStreak) initializeStudentData?.();
  }, [utmCoins, studentStreak, initializeStudentData]);

  const { items, orders, loading, err } = useCatalog();
  const [filter, setFilter] = useState("all");

  /* == Confirm de compra == */
  const [banner, setBanner] = useState({ type: "", msg: "" });
  const [confirm, setConfirm] = useState(null); // { item, qty, max }
  const [busyId, setBusyId] = useState(0);

  const coinsBase = toNumber(utmCoins?.cantidad_actual, 0);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => String(it.tipo || "").toLowerCase() === filter);
  }, [items, filter]);

  const openConfirm = (item) => {
    const price = toNumber(item.precio_coins, 0);
    const limit = item.limit_per_user ? Math.max(0, item.limit_per_user - toNumber(item.owned_count, 0)) : Infinity;
    const byCoins = price > 0 ? Math.floor(coinsBase / price) : 1;
    const byStock = item.stock != null ? Math.max(0, toNumber(item.stock, 0)) : Infinity;
    const max = Math.max(0, Math.min(limit, byCoins, byStock));
    if (max <= 0) {
      setBanner({ type: "warn", msg: "No puedes comprar este artículo (sin saldo, stock agotado o límite alcanzado)." });
      return;
    }
    setConfirm({ item, qty: 1, max });
  };

  const doBuy = async () => {
    if (!confirm?.item) return;
    try {
      setBusyId(confirm.item.id);
      const res = await buyStoreItem?.({ itemId: confirm.item.id, quantity: confirm.qty });
      const ok = Boolean(res?.ok ?? res?.success ?? res?.purchase?.id ?? res?.data?.purchase?.id ?? res?.id);
      if (ok) {
        setBanner({ type: "ok", msg: `Compra realizada: ${confirm.qty} × ${confirm.item.nombre}. ¡Disfrútalo!` });
        setConfirm(null);
        await refreshUTMCoins?.();
      } else {
        throw new Error(res?.message || "No se pudo completar la compra.");
      }
    } catch (e) {
      setBanner({ type: "err", msg: e?.message || "Error al comprar." });
    } finally {
      setBusyId(0);
    }
  };

  /* == Render de ítem == */
  const Tag = ({ children }) => <span className="pill">{children}</span>;

  const ItemCard = ({ it }) => {
    const Icon = ICONS[String(it.tipo || "").toLowerCase()] || Package;
    const price = toNumber(it.precio_coins, 0);
    const owned = toNumber(it.owned_count, 0);
    const limit = it.limit_per_user ? Math.max(0, it.limit_per_user - owned) : Infinity;
    const byCoins = price > 0 ? Math.floor(coinsBase / price) : 1;
    const byStock = it.stock != null ? Math.max(0, toNumber(it.stock, 0)) : Infinity;
    const max = Math.max(0, Math.min(limit, byCoins, byStock));
    const locked = max <= 0 || it.active === false;

    return (
      <div className={`item-card ${locked ? "locked" : ""}`}>
        <div className="item-icon"><Icon size={20} /></div>
        <div className="item-body">
          <div className="item-head">
            <h4 title={it.nombre}>{it.nombre}</h4>
            <div className="price">
              <Coins size={16} /> {pretty(price)}
            </div>
          </div>
          <p className="desc">{it.descripcion || "—"}</p>
          <div className="tags">
            {it.tipo ? <Tag>{String(it.tipo).charAt(0).toUpperCase() + String(it.tipo).slice(1)}</Tag> : null}
            {it.badge ? <Tag>{it.badge}</Tag> : null}
            {it.limit_per_user ? <Tag>Límite: {it.limit_per_user}</Tag> : null}
            {typeof it.stock === "number" ? <Tag>Stock: {it.stock}</Tag> : null}
            {owned > 0 ? <Tag><Check size={12} /> Ya tienes {owned}</Tag> : null}
            {it.active === false ? <Tag><Lock size={12} /> Inactivo</Tag> : null}
          </div>
        </div>

        <div className="item-cta">
          <button
            className="buy-btn"
            disabled={locked || busyId === it.id}
            onClick={() => openConfirm(it)}
            aria-label={`Comprar ${it.nombre}`}
          >
            <ShoppingCart size={16} />
            {locked ? "No disponible" : "Comprar"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="store-wrap" aria-live="polite" role="region" aria-label="Tienda UTMCoins">
      {/* Header simple: Volver a donde entró */}
      <header className="task-topbar">
        <button onClick={goBack} className="back-button" type="button">
          <ArrowLeft className="w-4 h-4" />
          <span className="hide-xs">Volver</span>
        </button>
      </header>

      {/* ===== HERO ===== */}
      <section className="store-hero">
        <div className="decor-grid" aria-hidden="true" />
        <div className="orb o1" aria-hidden="true" />
        <div className="orb o2" aria-hidden="true" />
        <div className="sheen" aria-hidden="true" />

        <div className="hero-card">
          <span className="ribbon">
            <Trophy size={16} aria-hidden="true" /> Tienda
          </span>

          <h1>Canjea tus <strong>UTMCoins</strong></h1>
          <p className="sub">Compra comodines y cosméticos para mejorar tu experiencia.</p>

          <div className="stats-row">
            <button
              type="button"
              className="coins-pill"
              title="UTMCoins disponibles"
              onClick={() => { }}
            >
              <Coins size={18} />
              <span className="coins-value">{pretty(coinsBase)}</span>
              <span className="coins-label">UTMCoins</span>
            </button>
          </div>

        </div>
      </section>

      {/* Banner de tienda (para compras), NO interfiere con el toast de racha */}
      {err && <div className="banner err"><strong>Error:</strong> {err}</div>}
      {banner.msg && <div className={`banner ${banner.type}`}>{banner.msg}</div>}

      {/* Filtros */}
      <div className="filters-row">
        <div className="left">
          <Info size={16} className="muted" /> <span className="muted">Los cosméticos no afectan el rendimiento académico.</span>
        </div>
        <div className="right">
          <Filter size={16} className="muted" />
          {["all", "comodin", "cosmético", "proteccion"].map(opt => (
            <button
              key={opt}
              className={`chip ${filter === opt ? "active" : ""}`}
              onClick={() => setFilter(opt)}
            >
              {opt === "all" ? "Todos" : opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Grid de artículos */}
      {loading ? (
        <div className="skeletons">
          <div className="sk-card" /><div className="sk-card" /><div className="sk-card" />
        </div>
      ) : filtered.length ? (
        <div className="grid">
          {filtered.map((it, idx) => (
            <ItemCard
              key={`item-${String(it.id ?? it.nombre ?? 'x')}-${idx}`}
              it={it}
            />
          ))}
        </div>
      ) : (
        <div className="empty">
          <Star className="icon" />
          <h3>Proximamente...</h3>
        </div>
      )}

      {/* Historial simple */}
      {!!orders?.length && (
        <section className="orders">
          <h3>Últimas compras</h3>
          <ul className="order-list">
            {orders.map((o, idx) => (
              <li
                key={`ord-${String(o.id ?? o.item_id ?? 'x')}-${String(o.fecha_compra ?? o.created_at ?? o.ts ?? 't')}-${idx}`}
                className="order-item"
              >
                <div className="oi-left">
                  <ShoppingCart size={16} /> <strong>{o.item_nombre || o.itemName || `Item ${idx + 1}`}</strong>
                  <span className="muted">×{o.cantidad || o.qty || 1}</span>
                </div>
                <div className="oi-right">
                  <Coins size={14} /> {pretty(
                    o.total_coins ?? o.totalCoins ?? (toNumber(o.precio_coins) * toNumber(o.cantidad ?? 1))
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Confirmación de compra */}
      {confirm && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h4>Confirmar compra</h4>
              <button className="icon-btn" onClick={() => setConfirm(null)} aria-label="Cerrar">✕</button>
            </div>
            <div className="modal-body">
              <p><strong>{confirm.item.nombre}</strong></p>
              <p className="muted">{confirm.item.descripcion}</p>

              <div className="qty-row">
                <label htmlFor="qty">Cantidad</label>
                <input
                  id="qty"
                  type="number"
                  min={1}
                  max={confirm.max}
                  value={confirm.qty}
                  onChange={(e) => {
                    const v = Math.max(1, Math.min(confirm.max, Number(e.target.value) || 1));
                    setConfirm((c) => ({ ...c, qty: v }));
                  }}
                />
                <span className="muted">máx. {confirm.max}</span>
              </div>

              <div className="resume">
                <span>Total</span>
                <span className="total">
                  <Coins size={14} /> {pretty(toNumber(confirm.item.precio_coins) * toNumber(confirm.qty))}
                </span>
              </div>
            </div>
            <div className="modal-actions">
              <button className="ghost" onClick={() => setConfirm(null)}>Cancelar</button>
              <button className="primary" onClick={doBuy} disabled={busyId === confirm.item.id}>
                {busyId === confirm.item.id ? "Procesando…" : "Confirmar compra"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        :root{
          --ex-bg-2:#0e162a; --ex-bg-3:#111827; --ex-emerald:#22c55e; --ex-emerald-dark:#16a34a;
          --ex-purple:#7c3aed; --ex-sky:#60a5fa; --ex-card:#0c1220cc; --ex-border:rgba(255,255,255,.12);
          --ex-text:#e6edf6; --ex-text-dim:#c7d2fe;

          --muted:#6b7280; --border:#e5e7eb; --text:#1f2937; --brand:#0f5fb8;
          --radius: 14px; --shadow-1:0 6px 24px rgba(0,0,0,.06);
        }
        .store-wrap{
  padding: clamp(16px,2vw,28px);
  width: min(1100px, calc(100vw - 20px)); /* más ancho sin pegarse a los bordes */
  max-width: 1600px;
  margin: 0 auto;
}


        .store-hero{
          position:relative; margin-bottom: 16px; border-radius: 24px;
          background: radial-gradient(1200px 600px at 10% -10%, rgba(16,185,129,0.15), transparent 60%),
                      radial-gradient(1000px 520px at 100% 0%, rgba(79,70,229,0.18), transparent 55%),
                      linear-gradient(135deg, #0f4c3a 0%, #1e3a5f 50%, #2d1b4e 100%);
          color: var(--ex-text); overflow:hidden; isolation:isolate;
          padding: clamp(20px, 3vw, 36px);
          box-shadow: 0 30px 80px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.06);
        }
        .hero-card{
          position:relative; z-index:2; border:1px solid var(--ex-border); border-radius:22px;
          background: linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,.02));
          padding: clamp(14px, 2vw, 22px);
          backdrop-filter: blur(10px) saturate(120%);
        }
        .ribbon{
          display:inline-flex; align-items:center; gap:8px; font-weight:800; font-size:12px;
          padding:6px 10px; border-radius:999px; color:#05130a;
          background: linear-gradient(135deg, var(--ex-emerald), #a7f3d0);
          box-shadow: 0 6px 18px rgba(34,197,94,.35), inset 0 1px 0 rgba(255,255,255,.5);
        }
        .hero-card h1{ margin:8px 0 4px; font-size: clamp(22px, 2.8vw + 12px, 36px); font-weight:900; }
        .sub{ color: var(--ex-text-dim); margin:0; }

        /* === TARJETAS DE STATS (CLARAS) — idénticas a CursoDetalle === */
        .stats-row{ display:flex; gap:12px; flex-wrap:wrap; margin-top:12px; }
        .stat-card{
          position:relative;
          background:#fff; border:1px solid var(--border);
          border-radius: 14px; padding:12px 14px; min-width: 210px;
          display:flex; align-items:center; gap:10px; box-shadow: var(--shadow-1);
        }
        .stat-btn{ cursor:pointer; transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease, background .12s ease; }
        .stat-btn:hover{ transform: translateY(-1px); box-shadow:0 10px 22px rgba(0,0,0,.08); border-color:#cbd5e1; background:#f8fafc; }
        .stat-icon{
          width:36px; height:36px; border-radius:999px; background:#f1f5ff;
          display:flex; align-items:center; justify-content:center; color:#0f5fb8;
        }
        .stat-value{ font-weight:800; color:#0f5fb8; line-height:1; }
        .stat-label{ color:#6b7280; font-size:.8rem; }

        .streak-badge{
          position:absolute; top:-10px; right:-10px;
          background:#0b1220; color:#e6edf6; border:1px solid rgba(255,255,255,.14);
          border-radius:999px; padding:5px 8px; font-size:.72rem; font-weight:900;
          box-shadow: 0 10px 30px rgba(0,0,0,.25);
        }

        .decor-grid{ position:absolute; inset:0; z-index:0; pointer-events:none;
          background-image: linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px);
          background-size: 28px 28px, 28px 28px;
          mask-image: radial-gradient(60% 60% at 50% 40%, #000 60%, transparent 100%);
        }
        .orb{ position:absolute; z-index:1; filter: blur(28px); opacity:.55; mix-blend-mode: screen; pointer-events:none; }
        .orb.o1{ width:420px; height:420px; border-radius:50%;
          background: radial-gradient(circle at 30% 30%, rgba(124,58,237,.55), transparent 60%),
                      radial-gradient(circle at 70% 70%, rgba(96,165,250,.55), transparent 60%);
          top:-120px; right:-120px;
        }
        .orb.o2{ width:360px; height:360px; border-radius:50%;
          background: radial-gradient(circle at 30% 30%, rgba(34,197,94,.55), transparent 60%),
                      radial-gradient(circle at 70% 70%, rgba(16,185,129,.45), transparent 60%);
          bottom:-120px; left:-100px;
        }
        .sheen{ position:absolute; inset:-2px; z-index:1; pointer-events:none; opacity:.6;
          background: radial-gradient(900px 260px at 10% -10%, rgba(34,197,94,.18), transparent 60%),
                      radial-gradient(900px 260px at 100% 0%, rgba(124,58,237,.18), transparent 55%);
        }

        /* Filtros + grid + resto (como tenías) */
        .filters-row{ display:flex; align-items:center; justify-content:space-between; gap:12px; margin: 14px 0 10px; flex-wrap:wrap; }
        .muted{ color: var(--muted); }
        .chip{
          border:1px solid var(--border); background:#fff; color:#374151; padding:6px 10px; border-radius:999px;
          font-size:.85rem; transition: all .2s ease; margin-left:6px;
        }
        .chip.active{ border-color:#c7d2fe; background:#eef2ff; color:#3730a3; font-weight:600; }
        .chip:hover{ transform: translateY(-1px); box-shadow: var(--shadow-1); }

        .grid{ display:grid; gap:14px; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); }
        .item-card{
          background:#fff; border:1px solid var(--border); border-radius: var(--radius); padding:12px;
          display:grid; grid-template-columns: 40px 1fr auto; gap:12px; align-items:center;
          box-shadow: var(--shadow-1);
          transition: transform .15s ease;
        }
        .item-card:hover{ transform: translateY(-2px); }
        .item-card.locked{ opacity:.75; }
        .item-icon{
          width:40px; height:40px; border-radius:12px; display:flex; align-items:center; justify-content:center;
          background:#f1f5ff; color:#0f5fb8; border:1px solid #e0e7ff;
        }
        .item-head{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
        .item-head h4{ margin:0; font-size:1rem; color:#0b3b7a; }
        .price{ display:inline-flex; align-items:center; gap:6px; font-weight:800; color:#047857; }
        .desc{ margin:6px 0 8px; color:#4b5563; font-size:.9rem; }
        .tags{ display:flex; gap:6px; flex-wrap:wrap; }
        .pill{ border:1px solid var(--border); background:#fff; color:#4b5563; font-size:.75rem; padding:3px 8px; border-radius:999px; font-weight:600; }
        .item-cta{ display:flex; align-items:center; }
        .buy-btn{
          height:36px; padding:0 12px; border-radius:10px; border:1px solid #2563eb; background:#2563eb; color:#fff; font-weight:700; display:inline-flex; gap:6px; align-items:center;
        }
        .buy-btn:disabled{ opacity:.6; cursor:not-allowed; }

        .coins-pill{
  display:inline-flex;
  align-items:center;
  gap:10px;
  padding:10px 14px;
  border-radius:999px;
  border:1px solid rgba(255,255,255,.18);
  background: rgba(255,255,255,.10);
  color: var(--ex-text);
  backdrop-filter: blur(10px) saturate(120%);
  box-shadow: 0 10px 24px rgba(0,0,0,.18), inset 0 1px 0 rgba(255,255,255,.10);
  cursor: default;
}
.coins-pill svg{ opacity:.95; }
.coins-value{
  font-weight:900;
  font-size:1.05rem;
  letter-spacing:.2px;
}
.coins-label{
  font-weight:800;
  font-size:.85rem;
  color: rgba(230,237,246,.82);
}


        .skeletons{ display:grid; gap:14px; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); }
        .sk-card{ height:92px; background: linear-gradient(90deg,#f2f3f5 25%, #f6f7f9 37%, #f2f3f5 63%); background-size: 400% 100%; animation: shimmer 1.4s infinite; border-radius: var(--radius); border:1px solid var(--border); }
        @keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }

        .empty{ text-align:center; background:#fff; border:1px dashed var(--border); border-radius: var(--radius); padding:24px; color: var(--muted); }
        .empty .icon{ width:42px; height:42px; color:#9ca3af; margin-bottom:8px; }

        .orders{ margin-top:18px; }
        .orders h3{ margin:0 0 10px; color:#0b3b7a; }
        .order-list{ list-style:none; padding:0; margin:0; display:grid; gap:8px; }
        .order-item{
          display:flex; align-items:center; justify-content:space-between; gap:10px; padding:10px 12px; border:1px dashed var(--border); border-radius:12px; background:#fff;
        }
        .oi-left{ display:flex; align-items:center; gap:8px; }
        .oi-right{ display:inline-flex; align-items:center; gap:6px; font-weight:800; color:#047857; }

        .banner{ margin:10px 0; padding:10px 12px; border-radius:12px; border:1px solid; }
        .banner.ok{ background:#ecfdf5; border-color:#a7f3d0; color:#065f46; }
        .banner.err{ background:#fef2f2; border-color:#fecaca; color:#991b1b; }
        .banner.warn{ background:#fffbeb; border-color:#fde68a; color:#92400e; }

        .modal-overlay{ position:fixed; inset:0; display:grid; place-items:center; background: rgba(0,0,0,.28); z-index:50; padding:12px; }
        .modal{ width:min(480px, 100%); border-radius:16px; background:#fff; border:1px solid var(--border); box-shadow: 0 30px 80px rgba(0,0,0,.35); }
        .modal-header{ padding:12px 14px; border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; }
        .modal-body{ padding:12px 14px; display:grid; gap:10px; }
        .modal-actions{ padding:12px 14px; display:flex; gap:8px; justify-content:flex-end; border-top:1px solid var(--border); }
        .icon-btn{ border:none; background:transparent; cursor:pointer; font-size:18px; }
        .qty-row{ display:flex; align-items:center; gap:10px; }
        .qty-row input{ width:90px; height:36px; padding:0 8px; border-radius:10px; border:1px solid var(--border); }
        .resume{ display:flex; align-items:center; justify-content:space-between; padding-top:6px; }
        .total{ display:inline-flex; align-items:center; gap:6px; font-weight:900; color:#047857; }
        .ghost{ height:36px; padding:0 12px; border-radius:10px; border:1px solid var(--border); background:#fff; }
        .primary{ height:36px; padding:0 12px; border-radius:10px; border:1px solid #2563eb; background:#2563eb; color:#fff; font-weight:700; }

        @media (max-width: 760px){
          .buy-btn{ width:100%; }
          .item-card{ grid-template-columns: 40px 1fr; }
          .item-cta{ grid-column: 1 / -1; }
          .stats-row .stat-card{ min-width: 160px; }
        }

        /* Header “Volver” */
.task-topbar{
  display:flex;
  align-items:center;
  margin-bottom: 12px;
}
.back-button{
  display:inline-flex;
  align-items:center;
  gap:10px;
  padding:10px 14px;
  border-radius:12px;
  border:1px solid rgba(0,0,0,.08);
  background:#fff;
  cursor:pointer;
  font-weight:800;
}
.back-button:hover{ transform: translateY(-1px); }
.hide-xs{ display:inline; }
@media (max-width: 480px){
  .hide-xs{ display:none; }
}
   @media (min-width: 1900px){
   .store-wrap{
     width: min(1100px, calc(100vw - 64px));
     max-width: 1600px;
   }
}

}


      `}</style>
    </div>
  );
};

export default Store;
